import { DataSourceSort } from 'custom_hooks/data_hooks/data_source/data_source';
import { SortState } from 'pages/Corporation/issues/utils';
import {
    DefaultCorporationOverviewState,
    loadCorporationOverviewState,
    saveCorporationOverviewState,
    StoredCorporationOverviewState,
} from 'pages/Corporation/state/shared';
import { CorporationOverviewFilters } from 'pages/Corporation/types';
import { ReactNode, useMemo } from 'react';
import {
    createZustandContext,
    getZustandStoreShallowHook,
    ZustandContextInitializer,
    ZustandContextProvider,
} from 'shared/zustand_context/zustand_context';

export type CorporationOverviewStateValues = Readonly<{
    favoritesFilter: 'favorites' | undefined;
    issuesWidgetSort: SortState;
    assetsWithAlertsSort: DataSourceSort[];
}>;

export type CorporationOverviewStateMethods = Readonly<{
    setFavoritesFilter: (filter: CorporationOverviewFilters['favoritesFilter']) => void;
    setIssuesWidgetSort: (sort: SortState) => void;
    setAssetsWithAlertsSort: (sort: DataSourceSort[]) => void;
}>;

export type CorporationOverviewState = CorporationOverviewStateValues &
    CorporationOverviewStateMethods;

const CorporationOverviewContext = createZustandContext<CorporationOverviewState>(
    'CorporationOverviewStore'
);

function getCorporationOverviewStateInitializer(
    corporationId: string,
    userEmail: string,
    storedState: StoredCorporationOverviewState | undefined
): ZustandContextInitializer<CorporationOverviewState> {
    return (set, get) => {
        const saveState = () => {
            const state = get();
            const stateToStore: StoredCorporationOverviewState = {
                favoritesFilter: state.favoritesFilter,
                issuesWidgetSort: state.issuesWidgetSort,
                assetsWithAlertsSort: state.assetsWithAlertsSort,
            };
            saveCorporationOverviewState(userEmail, corporationId, stateToStore);
        };

        const setAndSave = (partial: Partial<CorporationOverviewState>) => {
            set(partial);
            saveState();
        };

        return {
            favoritesFilter:
                storedState?.favoritesFilter ??
                DefaultCorporationOverviewState.favoritesFilter,
            issuesWidgetSort:
                storedState?.issuesWidgetSort ??
                DefaultCorporationOverviewState.issuesWidgetSort,
            assetsWithAlertsSort:
                storedState?.assetsWithAlertsSort ??
                DefaultCorporationOverviewState.assetsWithAlertsSort,

            setFavoritesFilter: (
                filter: CorporationOverviewFilters['favoritesFilter']
            ) => {
                setAndSave({ favoritesFilter: filter });
            },
            setIssuesWidgetSort: (sort: SortState) => {
                setAndSave({ issuesWidgetSort: sort });
            },
            setAssetsWithAlertsSort: (sort: DataSourceSort[]) => {
                setAndSave({ assetsWithAlertsSort: sort });
            },
        };
    };
}

export type CorporationOverviewStoreProviderProps = Readonly<{
    corporationId: string;
    userEmail: string;
    children: ReactNode;
}>;

export function CorporationOverviewStoreProvider({
    corporationId,
    userEmail,
    children,
}: CorporationOverviewStoreProviderProps) {
    // Recreate store when corporation or user changes
    const initializer = useMemo(() => {
        const storedState = loadCorporationOverviewState(userEmail, corporationId);
        return getCorporationOverviewStateInitializer(
            corporationId,
            userEmail,
            storedState
        );
    }, [corporationId, userEmail]);

    return (
        <ZustandContextProvider
            ctx={CorporationOverviewContext}
            initializer={initializer}
        >
            {children}
        </ZustandContextProvider>
    );
}

export const useCorporationOverviewStore = getZustandStoreShallowHook(
    CorporationOverviewContext
);
